/**
 * PlayerTrackPicker — track-selection modal used by PlayerOverlay + LiveTvOverlay.
 *
 * v1.3.96 — Reads available subtitle / audio tracks off the video element
 * (HTMLTextTrackList / HTMLAudioTrack via HLS.js when available) and lets
 * the user switch them. Also handles the "Playback settings" affordance
 * for playback speed (0.5x → 2x) which every VOD player should have.
 *
 * Three modes, controlled by `kind`:
 *   • "subtitles" — lists textTracks + an "Off" option.
 *   • "audio"     — lists audio tracks (HLS or native).
 *   • "settings"  — playback speed picker (0.5, 0.75, 1, 1.25, 1.5, 2).
 *
 * Kept dependency-free of shadcn Dialog so it also works inside the
 * fullscreen video element on TV browsers where portalled overlays
 * sometimes render off-screen.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { writePreferredAudioLang } from "../lib/audioLangPreference";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

// One selectable row — reused across all 3 picker modes for consistency.
// Hoisted out of the parent so React reuses its DOM node across renders
// (matches react/no-unstable-nested-components).
function Row({ testid, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between ${
        active
          ? "bg-orange-500/20 text-orange-300"
          : "text-white hover:bg-white/10"
      }`}
    >
      <span className="truncate">{label}</span>
      {active && <span className="text-orange-400 text-lg">•</span>}
    </button>
  );
}

export default function PlayerTrackPicker({
  kind,          // "subtitles" | "audio" | "settings"
  open,
  onClose,
  videoRef,
  hlsRef,        // optional HLS.js instance for audio-track switching
}) {
  const [tick, setTick] = useState(0); // force re-render when tracks change

  // Refresh the track list whenever the video reports new tracks or the
  // caller opens the picker — keeps the UI honest across stream swaps.
  useEffect(() => {
    if (!open) return;
    const v = videoRef?.current;
    if (!v) return;
    const bump = () => setTick((n) => n + 1);
    v.textTracks?.addEventListener?.("addtrack", bump);
    v.textTracks?.addEventListener?.("removetrack", bump);
    v.textTracks?.addEventListener?.("change", bump);
    v.audioTracks?.addEventListener?.("addtrack", bump);
    v.audioTracks?.addEventListener?.("removetrack", bump);
    return () => {
      v.textTracks?.removeEventListener?.("addtrack", bump);
      v.textTracks?.removeEventListener?.("removetrack", bump);
      v.textTracks?.removeEventListener?.("change", bump);
      v.audioTracks?.removeEventListener?.("addtrack", bump);
      v.audioTracks?.removeEventListener?.("removetrack", bump);
    };
  }, [open, videoRef]);

  const subtitleTracks = useMemo(() => {
    void tick;
    const v = videoRef?.current;
    const tracks = v?.textTracks ? Array.from(v.textTracks) : [];
    return tracks
      .filter((t) => t.kind === "subtitles" || t.kind === "captions")
      .map((t, i) => ({
        id: i,
        label: t.label || t.language || `Track ${i + 1}`,
        active: t.mode === "showing",
        ref: t,
      }));
  }, [tick, videoRef]);

  const audioTracks = useMemo(() => {
    void tick;
    const v = videoRef?.current;
    // Native audioTracks (Safari, MSE-backed)
    const native = v?.audioTracks ? Array.from(v.audioTracks) : [];
    if (native.length > 0) {
      return native.map((t, i) => ({
        id: i,
        label: t.label || t.language || `Track ${i + 1}`,
        lang: t.language || null,
        active: !!t.enabled,
        _kind: "native",
        ref: t,
      }));
    }
    // HLS.js-driven audio tracks
    const hls = hlsRef?.current;
    if (hls?.audioTracks?.length) {
      return hls.audioTracks.map((t) => ({
        id: t.id,
        label: t.name || t.lang || `Track ${t.id}`,
        lang: t.lang || null,
        active: t.id === hls.audioTrack,
        _kind: "hls",
      }));
    }
    return [];
  }, [tick, videoRef, hlsRef]);

  const currentSpeed = videoRef?.current?.playbackRate ?? 1;

  if (!open) return null;

  const title =
    kind === "subtitles" ? "Subtitles"
    : kind === "audio" ? "Audio track"
    : "Playback settings";

  const selectSubtitle = (chosen) => {
    const v = videoRef?.current;
    if (!v) return;
    Array.from(v.textTracks || []).forEach((t) => {
      if (t.kind === "subtitles" || t.kind === "captions") {
        t.mode = chosen && chosen.ref === t ? "showing" : "disabled";
      }
    });
    setTick((n) => n + 1);
  };

  const selectAudio = (t) => {
    if (t._kind === "hls" && hlsRef?.current) {
      hlsRef.current.audioTrack = t.id;
    } else if (t._kind === "native") {
      // Native path: only ONE track may be enabled at a time.
      const list = videoRef?.current?.audioTracks;
      if (list) {
        Array.from(list).forEach((a) => { a.enabled = a === t.ref; });
      }
    }
    // v1.3.97 — Persist the language so future streams auto-select the
    // same language when it's available. Users who binge-watch an
    // Italian series stop having to pick "ita" every episode.
    if (t.lang) writePreferredAudioLang(t.lang);
    setTick((n) => n + 1);
  };

  const selectSpeed = (rate) => {
    const v = videoRef?.current;
    if (v) v.playbackRate = rate;
    setTick((n) => n + 1);
  };

  // Renders one selectable row. Definition hoisted above the component.

  return (
    <div
      data-testid="track-picker-backdrop"
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="track-picker"
        className="w-[min(400px,90vw)] max-h-[80vh] rounded-2xl border border-slate-600 bg-slate-900 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <h2 className="text-white text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="track-picker-close"
            className="p-1.5 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-3 overflow-y-auto space-y-1">
          {kind === "subtitles" && (
            <>
              <Row
                testid="subtitle-track-off"
                label="Off"
                active={!subtitleTracks.some((t) => t.active)}
                onClick={() => selectSubtitle(null)}
              />
              {subtitleTracks.length === 0 && (
                <div className="text-slate-500 text-sm px-4 py-6 text-center">
                  This stream doesn&apos;t carry any subtitle tracks.
                </div>
              )}
              {subtitleTracks.map((t) => (
                <Row
                  key={t.id}
                  testid={`subtitle-track-${t.id}`}
                  label={t.label}
                  active={t.active}
                  onClick={() => selectSubtitle(t)}
                />
              ))}
            </>
          )}
          {kind === "audio" && (
            <>
              {audioTracks.length === 0 ? (
                <div className="text-slate-500 text-sm px-4 py-6 text-center">
                  This stream reports a single audio track only.
                </div>
              ) : (
                audioTracks.map((t) => (
                  <Row
                    key={t.id}
                    testid={`audio-track-${t.id}`}
                    label={t.label}
                    active={t.active}
                    onClick={() => selectAudio(t)}
                  />
                ))
              )}
            </>
          )}
          {kind === "settings" && (
            <>
              <div className="text-slate-400 text-xs uppercase tracking-wider px-4 pt-2 pb-1">
                Playback speed
              </div>
              {SPEEDS.map((rate) => (
                <Row
                  key={rate}
                  testid={`speed-${rate}`}
                  label={`${rate}x${rate === 1 ? " (normal)" : ""}`}
                  active={Math.abs(currentSpeed - rate) < 0.01}
                  onClick={() => selectSpeed(rate)}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
